import React from 'react';
import { Card, Row, Col, Statistic, List, Typography, Button, Space, Progress, Tag } from 'antd';
import {
  ShoppingOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const Dashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data: dashboardData, isLoading } = useQuery(
    'provider-dashboard',
    async () => {
      const response = await api.get('/provider/dashboard');
      return response.data?.data || response.data || {};
    },
    {
      refetchInterval: 30000,
      enabled: !!user && !!localStorage.getItem('token'),
    },
  );

  const { data: bookingsData } = useQuery(
    'provider-bookings',
    async () => {
      const response = await api.get('/provider/my-bookings');
      return response.data?.data || response.data || [];
    },
    {
      enabled: !!user && !!localStorage.getItem('token'),
    },
  );

  const { data: servicesData } = useQuery(
    'provider-services',
    async () => {
      const response = await api.get('/provider/services');
      return response.data?.data || response.data || [];
    },
    {
      enabled: !!user && !!localStorage.getItem('token'),
    },
  );

  const stats = dashboardData || {};
  const bookings = bookingsData || [];
  const services = servicesData || [];

  // Use dashboard stats for counts (more accurate from backend)
  const totalServices = stats.total_services ?? services.length;
  const pendingBookings =
    stats.pending_bookings ??
    bookings.filter(
      (b) => b.status === 'pending' || b.status === 'confirmed' || b.status === 'in_progress',
    ).length;
  const completedBookings =
    stats.completed_bookings ?? bookings.filter((b) => b.status === 'completed').length;

  return (
    <div>
      <Title level={2} className="page-title">
        Welcome back, {user?.full_name || user?.name || user?.business_name || 'Provider'}!
      </Title>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Total Services"
              value={totalServices}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Pending Bookings"
              value={pendingBookings}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Completed Jobs"
              value={completedBookings}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Wallet Balance"
              value={stats.wallet_balance || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#722ed1' }}
              precision={2}
              formatter={(value) => `Rs. ${value}`}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Pending Bookings */}
        <Col xs={24} lg={12}>
          <Card
            title="Pending Bookings"
            extra={
              <Button type="link" onClick={() => navigate('/bookings')}>
                View All
              </Button>
            }
          >
            {bookings.length > 0 ? (
              <List
                dataSource={bookings.slice(0, 5)}
                loading={isLoading}
                renderItem={(booking) => (
                  <List.Item>
                    <List.Item.Meta
                      title={booking.product_service?.name || 'Service'}
                      description={
                        <div>
                          <Text>{booking.user?.full_name || 'Customer'}</Text>
                          <br />
                          <Text type="secondary">
                            {booking.booking_date
                              ? new Date(booking.booking_date).toLocaleDateString()
                              : ''}{' '}
                            at {booking.booking_time}
                          </Text>
                        </div>
                      }
                    />
                    <Tag color={booking.status === 'in_progress' ? 'processing' : 'default'}>
                      {booking.status}
                    </Tag>
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">No pending bookings</Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Quick Actions */}
        <Col xs={24} lg={12}>
          <Card title="Quick Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                block
                size="large"
                icon={<ShoppingOutlined />}
                onClick={() => navigate('/products')}
              >
                Add Products/Services
              </Button>
              <Button
                block
                size="large"
                icon={<ClockCircleOutlined />}
                onClick={() => navigate('/bookings')}
              >
                View Bookings ({pendingBookings})
              </Button>
              <Button
                block
                size="large"
                icon={<DollarOutlined />}
                onClick={() => navigate('/earnings')}
              >
                View Earnings
              </Button>
              <Button
                block
                size="large"
                icon={<CheckCircleOutlined />}
                onClick={() => navigate('/assessment-tasks')}
              >
                Home Assessment Tasks
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Services Overview */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            title="My Services"
            extra={
              <Button type="link" onClick={() => navigate('/products')}>
                Manage
              </Button>
            }
          >
            {services.length > 0 ? (
              <Row gutter={[16, 16]}>
                {services.slice(0, 4).map((service) => (
                  <Col xs={12} sm={6} key={service.id}>
                    <Card size="small">
                      <Statistic
                        title={service.name}
                        value={service.price}
                        prefix={<DollarOutlined />}
                        valueStyle={{ fontSize: 16 }}
                        formatter={(value) => `Rs. ${value}`}
                      />
                      <Tag
                        color={service.is_available ? 'green' : 'default'}
                        style={{ marginTop: 8 }}
                      >
                        {service.is_available ? 'Active' : 'Inactive'}
                      </Tag>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">No services yet. Add your first service!</Text>
                <br />
                <Button
                  type="primary"
                  style={{ marginTop: 16 }}
                  onClick={() => navigate('/products')}
                >
                  Add Service
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Performance */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Performance">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Completed Orders</Text>
                <Progress
                  percent={
                    stats.total_orders > 0
                      ? Math.round((stats.completed_orders / stats.total_orders) * 100)
                      : 0
                  }
                  status="active"
                  strokeColor="#52c41a"
                  format={(percent) =>
                    `${stats.completed_orders || 0} / ${stats.total_orders || 0} (${percent}%)`
                  }
                />
              </div>
              <div>
                <Text strong>Completed Bookings</Text>
                <Progress
                  percent={
                    stats.total_bookings > 0
                      ? Math.round((stats.completed_bookings / stats.total_bookings) * 100)
                      : 0
                  }
                  status="active"
                  strokeColor="#1890ff"
                  format={(percent) =>
                    `${stats.completed_bookings || 0} / ${stats.total_bookings || 0} (${percent}%)`
                  }
                />
              </div>
              <div>
                <Text strong>Average Rating</Text>
                <Progress
                  percent={(stats.average_rating || 0) * 20}
                  status="active"
                  strokeColor="#faad14"
                  format={(percent) => `${(percent / 20).toFixed(1)} ★`}
                />
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Account Info">
            <Row gutter={16}>
              <Col span={24}>
                <Text type="secondary">Status</Text>
                <div>
                  <Tag color="green">Active</Tag>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
